import * as _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { MenuChangeEvent } from '@szhsin/react-menu';
import type { VoidFunctionComponent } from 'react';

import { TEAM_GROUPS } from '../../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { useTeamGroups, useTeams } from '../../../../hooks/api';
import TeamsModule from '../../../../redux/teams';
import { useHasPermission } from '../../../../shared/permissions';
import { Icon, icons, IconText, RouterLink } from '../../../generic';
import {
  Menu,
  MenuDivider,
  MenuGroup,
  MenuHeader,
  MenuItem,
  SearchMenuItem,
} from '../../../generic/Menu';
import type { FullTeamGroupResponse } from '../../../../../shared/types/routes/teamGroups';
import type { TeamWithAssociations } from '../../../../../shared/types/routes/teams';
import type { Permission } from '../../../../../typings/permissions';
import type { TeamSelection } from '../../../../types';

import TeamGroupMenuItem from './TeamGroupMenuItem';
import TeamMenuItem from './TeamMenuItem';
import TeamSelectionMenuLoading from './TeamSelectionMenuLoading';
import TeamSelectionMenuSelected from './TeamSelectionMenuSelected';

import styles from './TeamSelectionMenu.module.less';

const VIEW_ANY_PROJECT: Permission = {
  resourceType: ResourceType.ProjectAny,
  action: Action.View,
};

const VIEW_ANY_TEAM: Permission = {
  resourceType: ResourceType.TeamAny,
  action: Action.View,
};

//  Helper for filtering teams by name (case-insensitive).
const filterTeams = (teams: TeamWithAssociations[], search: string): TeamWithAssociations[] => {
  const searchLower = search.toLowerCase();
  return teams.filter((team) => team.name.toLowerCase().includes(searchLower));
};

//  Helper for filtering team groups by name (case-insensitive).
const filterTeamGroups = (teamGroups: FullTeamGroupResponse[], search: string): FullTeamGroupResponse[] => {
  const searchLower = search.toLowerCase();
  return teamGroups.filter((teamGroup) => teamGroup.name.toLowerCase().includes(searchLower));
};

//  Helper for getting the userIds associated with a team.
const getTeamUserIds = (team: TeamWithAssociations): Set<number> => {
  const users = team.teamUsers ?? [];
  return new Set(users.map((user) => user.userId));
};

//  Helper for getting the userIds associated with a team group.
const getTeamGroupUserIds = (teamGroup: FullTeamGroupResponse): Set<number> => {
  const users = teamGroup.members ?? [];
  return new Set(users.map((user) => user.userId));
};

type Props = {
  /**
   * Callback for trigger side-effects when the team selection changes.
   */
  onChange?: () => void;
};

/**
 * A dropdown, multi-selection menu for selecting teams.
 * This component is responsible for fetching and sorting/displaying the teams,
 * as well as for setting the selected team(s), which is all stored in Redux so
 * it can be accessed by other components.
 */
const TeamSelectionMenu: VoidFunctionComponent<Props> = ({ onChange }) => {
  const dispatch = useDispatch();
  const userId = window.user?.id as number;
  const isTeamGroupsEnabled = useIsFeatureEnabled(TEAM_GROUPS);
  //  Switch to enable fetching teams + team groups, but only when the menu is opened.
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  //  Search state
  const [search, setSearch] = useState<string>('');
  const isSearching = search.length !== 0;

  //  Teams + Team Groups state
  const { data: teams, isLoading: isFetchingTeams } = useTeams(isMenuOpen);
  const { data: teamGroups, isLoading: isFetchingTeamGroups } = useTeamGroups(isTeamGroupsEnabled && isMenuOpen);
  const teamSelectionRedux = useSelector(TeamsModule.selectors.getTeamSelection);
  //  Turn the selection into a Set of ID (if it's not null)
  //  Note that we can't actually make TeamSelection a real Set, because Redux doesn't support non-serializable values.
  const teamSelection = useMemo(() => {
    if (teamSelectionRedux === 'null') {
      return teamSelectionRedux;
    }
    return new Set(teamSelectionRedux);
  }, [teamSelectionRedux]);

  const filteredTeams = useMemo(() => filterTeams(teams || [], search), [teams, search]);
  const filteredTeamGroups = useMemo(() => filterTeamGroups(teamGroups || [], search), [teamGroups, search]);

  const [allMyTeams, allOtherTeams] = _.partition(filteredTeams, (team) => getTeamUserIds(team).has(userId));
  const [allMyTeamGroups, allOtherTeamGroups] = _.partition(filteredTeamGroups,
    (teamGroup) => getTeamGroupUserIds(teamGroup).has(userId));
  const myTeams = _.sortBy(allMyTeams, (team) => team.name.toLowerCase());
  const otherTeams = _.sortBy(allOtherTeams, (team) => team.name.toLowerCase());
  const myTeamGroups = _.sortBy(allMyTeamGroups, (teamGroup) => teamGroup.name.toLowerCase());
  const otherTeamGroups = _.sortBy(allOtherTeamGroups, (teamGroup) => teamGroup.name.toLowerCase());

  const isTeamSelected = useCallback((teamId: number): boolean => {
    if (teamSelection === 'null') {
      return false;
    }
    return teamSelection.has(teamId);
  }, [teamSelection]);

  //  Combine teams + team groups into a single array
  const allTeams = useMemo(() => [
    ...(teams || []),
    ...(teamGroups || []),
  ].map((team) => ({
    id: team.id,
    name: team.name,
  })), [teams, teamGroups]);

  //  Permissions
  const hasPermission = useHasPermission();
  const canViewAnyProject = hasPermission(VIEW_ANY_PROJECT);
  const canViewAnyTeam = hasPermission(VIEW_ANY_TEAM);

  // Dispatch the selection action to the Teams store and fire the onChange handler, if present.
  const setTeamSelection = (selection: TeamSelection) => {
    dispatch(TeamsModule.actions.setTeamSelection(selection));
    if (onChange) {
      onChange();
    }
  };

  //  Select a team or team group (team groups are treated as a single selection).
  const selectTeam = (teamId: number): void => {
    // If the selection is currently 'null' (unassigned), select just this team
    if (teamSelection === 'null') {
      setTeamSelection([teamId]);
      return;
    }

    if (teamSelection.has(teamId)) {
      const updatedSelection = [...teamSelection].filter((selectedTeamId) => selectedTeamId !== teamId);
      // Remove the team from the current selection if present
      setTeamSelection(updatedSelection);
    } else {
      // Otherwise add it to the selection
      setTeamSelection([...teamSelection, teamId]);
    }
  };

  return (
    <Menu
      menuButton={(
        <div className={styles.button}>
          <span className={styles.buttonLabel}>
            <TeamSelectionMenuSelected
              teams={allTeams}
              teamSelection={teamSelection}
            />
          </span>
          <span className={styles.buttonIcon}>
            <Icon icon={icons.chevronDown} />
          </span>
        </div>
      )}
      menuClassName={styles.menu}
      onMenuChange={(event: MenuChangeEvent) => {
        if (event.open) {
          setSearch('');
          //  One-time switch to enable fetching teams
          if (!isMenuOpen) {
            setIsMenuOpen(true);
          }
        }
      }}
    >
      <SearchMenuItem placeholder="Search teams..." onChange={(e) => setSearch(e.target.value)} />
      {!isSearching && (canViewAnyProject || canViewAnyTeam) && (
        <MenuDivider className={styles.divider} />
      )}
      {!isSearching && canViewAnyProject && (
        <>
          <TeamMenuItem
            name="All projects"
            isSelected={teamSelection !== 'null' && teamSelection.size === 0}
            onClick={() => setTeamSelection([])}
          />
          <TeamMenuItem
            name="Unassigned projects"
            isSelected={teamSelection === 'null'}
            onClick={() => setTeamSelection('null')}
          />
        </>
      )}
      <MenuDivider className={styles.divider} />
      <MenuGroup className={styles.menuGroup}>
        {isTeamGroupsEnabled && (
          <>
            <MenuHeader>My Team Groups</MenuHeader>
            {isFetchingTeamGroups && <TeamSelectionMenuLoading />}
            {myTeamGroups.length > 0 ? (
              myTeamGroups.map((teamGroup) => (
                <TeamGroupMenuItem
                  key={teamGroup.id}
                  teamGroup={teamGroup}
                  isTeamSelected={isTeamSelected}
                  selectTeam={selectTeam}
                />
              ))
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment -- It's fine here
              <>{!isFetchingTeamGroups && <MenuItem disabled>No team groups found</MenuItem>}</>
            )}
            <MenuDivider className={styles.divider} />
          </>
        )}

        <MenuHeader>My Teams</MenuHeader>
        {isFetchingTeams && <TeamSelectionMenuLoading />}
        {myTeams.length > 0 ? (
          myTeams.map((team) => (
            <TeamMenuItem
              key={team.id}
              name={team.name}
              count={team.teamProjectsCount}
              isSelected={isTeamSelected(team.id)}
              onClick={() => selectTeam(team.id)}
            />
          ))
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment -- It's fine here
          <>{!isFetchingTeams && <MenuItem disabled>No teams found</MenuItem>}</>
        )}
        <MenuDivider className={styles.divider} />
        {isTeamGroupsEnabled && (
          <>
            <MenuHeader>Other Team Groups</MenuHeader>
            {isFetchingTeamGroups && <TeamSelectionMenuLoading />}
            {otherTeamGroups.length > 0 ? (
              otherTeamGroups.map((teamGroup) => (
                <TeamGroupMenuItem
                  key={teamGroup.id}
                  teamGroup={teamGroup}
                  isTeamSelected={isTeamSelected}
                  selectTeam={selectTeam}
                />
              ))
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment -- It's fine here
              <>{!isFetchingTeamGroups && <MenuItem disabled>No teams found</MenuItem>}</>
            )}
            <MenuDivider className={styles.divider} />
          </>
        )}
        <MenuHeader>Other Teams</MenuHeader>
        {isFetchingTeams && <TeamSelectionMenuLoading />}
        {otherTeams.length > 0 ? (
          otherTeams.map((team) => (
            <TeamMenuItem
              key={team.id}
              name={team.name}
              count={team.teamProjectsCount}
              isSelected={isTeamSelected(team.id)}
              onClick={() => selectTeam(team.id)}
            />
          ))
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment -- It's fine here
          <>{!isFetchingTeams && <MenuItem disabled>No teams found</MenuItem>}</>
        )}
      </MenuGroup>
      {canViewAnyTeam && (
        <>
          <MenuDivider className={styles.divider} />
          <MenuItem className={styles.itemName}>
            <RouterLink to="/account/settings/organization/teams">
              <IconText icon={icons.gear} text="Manage Teams" />
            </RouterLink>
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

export default TeamSelectionMenu;
